import { useState } from 'react'
import { Sparkles, Loader2, X, Check } from 'lucide-react'
import { pocApi } from '@/api/client'
import { showToast } from '@/store/uiStore'
import { TranscriptSourcePanel } from '@features/scope/components/TranscriptSourcePanel'
import type { DealTranscript, HubspotExcerpt } from '@api/client'
import type { PocRecord, CheckType } from '../types'

interface AiSuggestion {
  value: string | null
  confidence: 'high' | 'medium' | 'low'
  source: string
}

interface AiAssistResult {
  suggestions: Record<string, AiSuggestion>
}

const FIELD_LABELS: Record<string, { label: string; target: 'poc' | 'discovery' | 'context' }> = {
  contact_name: { label: 'Contact', target: 'discovery' },
  check_types: { label: 'Check Types', target: 'poc' },
  countries_in_scope: { label: 'Countries in Scope', target: 'poc' },
  record_count: { label: 'Number of Records', target: 'poc' },
  problem: { label: 'Problem to Solve', target: 'discovery' },
  current_pass_rate: { label: 'Current Pass Rate', target: 'poc' },
  success_criteria: { label: 'Success Criteria', target: 'poc' },
  current_vendor: { label: 'Current Vendor', target: 'discovery' },
  current_pain_points: { label: 'Current Pain Points', target: 'discovery' },
  industry: { label: 'Industry', target: 'context' },
  background: { label: 'Background', target: 'context' },
  problems: { label: 'Problems to Solve', target: 'context' },
  timeline: { label: 'Timeline', target: 'discovery' },
  decision_criteria: { label: 'Decision Criteria', target: 'discovery' },
  business_case_impact: { label: 'Business Impact', target: 'discovery' },
  manual_review_staff: { label: 'Manual Review Staff', target: 'discovery' },
  monthly_volume: { label: 'Monthly Volume', target: 'discovery' },
  poc_timeline: { label: 'POC Timeline', target: 'discovery' },
  poc_cost: { label: 'POC Cost', target: 'discovery' },
}

const CONFIDENCE_CONFIG = {
  high: { label: 'High', bg: 'bg-emerald-500/20', color: 'text-emerald-400' },
  medium: { label: 'Medium', bg: 'bg-amber-500/20', color: 'text-amber-400' },
  low: { label: 'Low', bg: 'bg-red-500/20', color: 'text-red-400' },
}

interface PocAiAssistProps {
  poc: PocRecord
  scopeDealId?: number | null
  onUpdate: (data: Partial<PocRecord>) => void
}

export function PocAiAssist({ poc, scopeDealId = null, onUpdate }: PocAiAssistProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notes, setNotes] = useState('')
  const [result, setResult] = useState<AiAssistResult | null>(null)
  const [applied, setApplied] = useState<Set<string>>(new Set())

  // TranscriptSourcePanel state
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<Set<number>>(new Set())
  const [includeExcerpt, setIncludeExcerpt] = useState(true)
  const [hubspotExcerpt, setHubspotExcerpt] = useState<HubspotExcerpt | null>(null)
  const [excerptLoading, setExcerptLoading] = useState(false)

  const toggleTranscript = (id: number) => {
    setSelectedTranscriptIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleTranscriptsLoaded = (transcripts: DealTranscript[]) => {
    setSelectedTranscriptIds(new Set(transcripts.map(t => t.id)))
  }

  const hasInput = notes.trim() || selectedTranscriptIds.size > 0 || (includeExcerpt && hubspotExcerpt)

  const handleGenerate = async () => {
    setLoading(true)
    setResult(null)
    setApplied(new Set())
    try {
      const res = await pocApi.aiAssist(poc.id, {
        notes: notes.trim() || undefined,
        transcript_ids: Array.from(selectedTranscriptIds),
        include_excerpt: includeExcerpt && !!hubspotExcerpt,
      })
      const data = ((res?.data as any)?.data ?? res?.data) as AiAssistResult | undefined
      if (data?.suggestions) {
        setResult(data)
      } else {
        showToast('Failed to generate suggestions', 'error')
      }
    } catch (err: any) {
      showToast(err?.message || 'Failed to generate suggestions', 'error')
    }
    setLoading(false)
  }

  const handleApply = (field: string, value: string) => {
    const fieldInfo = FIELD_LABELS[field]
    if (!fieldInfo) return

    if (field === 'check_types') {
      const types = value.split(',').map(s => s.trim()).filter(Boolean) as CheckType[]
      onUpdate({ check_types: types })
    } else if (field === 'countries_in_scope') {
      const codes = value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
      onUpdate({ countries_in_scope: codes })
    } else if (field === 'record_count') {
      onUpdate({ record_count: parseInt(value) || null })
    } else if (field === 'current_pass_rate') {
      onUpdate({ current_pass_rate: parseFloat(value) || null })
    } else if (field === 'success_criteria') {
      onUpdate({ success_criteria: value })
    } else if (fieldInfo.target === 'context') {
      const current = poc.discovery_json as unknown as Record<string, unknown> || {}
      const discKey = `comms_${field}`
      onUpdate({ discovery_json: { ...current, [discKey]: value } as unknown as PocRecord['discovery_json'] })
    } else if (fieldInfo.target === 'discovery') {
      const current = poc.discovery_json as unknown as Record<string, unknown> || {}
      onUpdate({ discovery_json: { ...current, [field]: value } as unknown as PocRecord['discovery_json'] })
    }

    setApplied(prev => new Set([...prev, field]))
  }

  const getCurrentValue = (field: string): string => {
    if (field === 'check_types') return (poc.check_types || []).join(', ')
    if (field === 'countries_in_scope') return (poc.countries_in_scope || []).join(', ')
    if (field === 'record_count') return poc.record_count ? String(poc.record_count) : ''
    if (field === 'current_pass_rate') return poc.current_pass_rate != null ? `${poc.current_pass_rate}%` : ''
    if (field === 'success_criteria') return poc.success_criteria || ''
    const discovery = poc.discovery_json as unknown as Record<string, unknown> || {}
    const fieldInfo = FIELD_LABELS[field]
    if (fieldInfo?.target === 'context') {
      return (discovery[`comms_${field}`] as string) || ''
    }
    return (discovery[field] as string) || ''
  }

  const suggestedFields = result
    ? Object.entries(result.suggestions).filter(([, s]) => s.value != null)
    : []

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded-lg hover:bg-violet-500/20 transition-colors">
        <Sparkles size={14} />
        AI Assist
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-[var(--bg-secondary)] rounded-lg shadow-modal" role="dialog" aria-modal="true">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/20 bg-violet-500/5 rounded-t-lg">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-400">AI-Assisted POC Intake</span>
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-500/20 text-violet-300 rounded">SUGGESTIONS ONLY</span>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
                <p className="text-xs text-[var(--text-secondary)]">
                  Select transcripts from the linked deal and paste any additional notes. The AI will analyse them to <strong>suggest</strong> values for the POC request form — check types, countries, pass rates, success criteria, and discovery fields. You review and accept each suggestion individually.
                </p>

                {/* Shared TranscriptSourcePanel */}
                <TranscriptSourcePanel
                  dealId={scopeDealId}
                  isActive={open}
                  selectedTranscriptIds={selectedTranscriptIds}
                  onToggleTranscript={toggleTranscript}
                  onTranscriptsLoaded={handleTranscriptsLoaded}
                  includeExcerpt={includeExcerpt}
                  onToggleExcerpt={setIncludeExcerpt}
                  hubspotExcerpt={hubspotExcerpt}
                  onExcerptChange={setHubspotExcerpt}
                  excerptLoading={excerptLoading}
                  onExcerptLoadingChange={setExcerptLoading}
                />

                {/* Additional notes */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Additional Notes (optional)</p>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Paste extra context — email threads, Slack messages, AE intel, or anything not in a saved transcript..."
                    className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-end">
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !hasInput}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing...</> : <><Sparkles className="w-3.5 h-3.5" /> {result ? 'Regenerate' : 'Generate suggestions'}</>}
                  </button>
                </div>

                {/* Results */}
                {result && (
                  <div className="space-y-3 pt-3 border-t border-violet-500/20">
                    {suggestedFields.length === 0 ? (
                      <p className="text-xs text-[var(--text-muted)] italic">No suggestions could be extracted from the provided material.</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                            {suggestedFields.length} suggestion{suggestedFields.length !== 1 ? 's' : ''}
                          </span>
                          {suggestedFields.filter(([, s]) => s.confidence !== 'low').length > 1 && suggestedFields.some(([f, s]) => !applied.has(f) && s.confidence !== 'low') && (
                            <button
                              onClick={() => {
                                // Batch all updates to avoid discovery_json overwrites
                                const pocUpdates: Partial<PocRecord> = {}
                                let discAccum = { ...(poc.discovery_json as unknown as Record<string, unknown> || {}) }
                                const newApplied = new Set(applied)

                                for (const [field, s] of suggestedFields) {
                                  if (applied.has(field) || !s.value || s.confidence === 'low') continue
                                  const fi = FIELD_LABELS[field]
                                  if (!fi) continue

                                  if (field === 'check_types') {
                                    pocUpdates.check_types = s.value.split(',').map(x => x.trim()).filter(Boolean) as CheckType[]
                                  } else if (field === 'countries_in_scope') {
                                    pocUpdates.countries_in_scope = s.value.split(',').map(x => x.trim().toUpperCase()).filter(Boolean)
                                  } else if (field === 'record_count') {
                                    pocUpdates.record_count = parseInt(s.value) || null
                                  } else if (field === 'current_pass_rate') {
                                    pocUpdates.current_pass_rate = parseFloat(s.value) || null
                                  } else if (field === 'success_criteria') {
                                    pocUpdates.success_criteria = s.value
                                  } else if (fi.target === 'context') {
                                    discAccum[`comms_${field}`] = s.value
                                  } else if (fi.target === 'discovery') {
                                    discAccum[field] = s.value
                                  }
                                  newApplied.add(field)
                                }

                                // Single batched update
                                onUpdate({ ...pocUpdates, discovery_json: discAccum as unknown as PocRecord['discovery_json'] })
                                setApplied(newApplied)
                              }}
                              className="text-[10px] font-medium text-violet-400 hover:text-violet-300 transition-colors"
                            >
                              Accept all
                            </button>
                          )}
                        </div>

                        {suggestedFields.map(([field, suggestion]) => {
                          const fieldInfo = FIELD_LABELS[field]
                          if (!fieldInfo || !suggestion.value) return null
                          const confCfg = CONFIDENCE_CONFIG[suggestion.confidence] || CONFIDENCE_CONFIG.low
                          const isApplied = applied.has(field)
                          const currentVal = getCurrentValue(field)

                          return (
                            <div
                              key={field}
                              className={`rounded-lg border p-3 ${
                                isApplied
                                  ? 'border-emerald-500/30 bg-emerald-500/5'
                                  : 'border-[var(--border-color)] bg-[var(--bg-primary)]'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{fieldInfo.label}</span>
                                  <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${confCfg.bg} ${confCfg.color}`}>
                                    {confCfg.label}
                                  </span>
                                </div>
                                {isApplied ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                                ) : suggestion.confidence === 'low' ? (
                                  <span className="text-[10px] text-red-400/80 italic">Needs more info</span>
                                ) : (
                                  <button
                                    onClick={() => handleApply(field, suggestion.value!)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors"
                                  >
                                    Use this suggestion
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-[var(--text-primary)] whitespace-pre-wrap">{suggestion.value}</p>
                              <p className="text-[10px] text-[var(--text-muted)] mt-1 italic">Source: {suggestion.source}</p>
                              {currentVal && !isApplied && (
                                <div className="mt-2 pt-2 border-t border-[var(--border-color)]">
                                  <p className="text-[10px] text-[var(--text-muted)]">Current value:</p>
                                  <p className="text-[11px] text-[var(--text-secondary)]">{currentVal}</p>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
